import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBasicEventInfoQuery } from './basicEventInfo.graphql.generated';
import { useBasicEventInfoByIdQuery } from './basicEventInfoById.graphql.generated';
import { useSegmentGroupCallForEvent } from '@shared/core/analytics/useSegmentGroupCall';
import { EXCLUDED_EVENT_HANDLES } from '@shared/constants/excludedEventHandles';

import type { EventInfo, EventInfoData } from './EventInfo.types';
import { InternalEventInfoProvider } from './internalEventInfoContext';
import { useIdentify } from '@shared/core/analytics/useIdentify';
import { useEventUserRole } from '@shared/components/AuthProvider/AuthProvider.utils';
import { isInIframe } from '@shared/utils/isInIframe';
import type { EventType, EventWebsiteState } from '@graphql/generated-base';
import globalWindow from '@shared/core/globals';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useLocation, matchPath } from '@react-router';

const isPreviewing = isInIframe();

/**
 * Routes that lack an eventHandle in the URL but still need event context
 * for feature-flag bucketing. The `?eventId=` / `?event-id=` query param is
 * ONLY honoured when the current pathname matches one of these patterns —
 * handle-based routes always resolve via the handle and ignore a stray param.
 */
const EVENT_ID_PARAM_ALLOWED_ROUTES: ReadonlyArray<string> = [
  '/travel/checkout',
  '/travel/car/checkout',
  '/travel/car/reservations/:reservationId',
  '/travel/reservations/:reservationId',
  '/travel/:placeId',
  '/stays/:joyPlaceId',
  '/ecard',
  '/contact/:magicLinkId',
  '/shop',
  '/card/:ecardId',
];

type EventInfoProviderProps = Readonly<{
  eventHandle: string | undefined;
  initialEventId?: string;
  initialEventFeatureFlags?: ReadonlyArray<string>;
  initialEventWebsiteState?: EventWebsiteState;
  initialEventType?: string;
}>;

const useSsrEventInfo = () => {
  // Capture SSR-injected window globals into refs on first render.
  // Refs ensure the values persist across re-renders without triggering updates.
  const eventHandleRef = useRef(globalWindow.__INITIAL_EVENT_HANDLE_FOR_EVENT_INFO_PROVIDER__);
  const eventIdRef = useRef(globalWindow.__INITIAL_EVENT_ID__);
  const eventWebsiteStateRef = useRef(globalWindow.__INITIAL_EVENT_WEBSITE_STATE__);
  const eventFeatureFlagsRef = useRef(globalWindow.__INITIAL_EVENT_FEATURE_FLAGS__);
  const eventTypeRef = useRef(globalWindow.__INITIAL_EVENT_TYPE__);

  // Clean up window globals after capture. Moved to useEffect to avoid side effects
  // during render (React Strict Mode and concurrent rendering can call render multiple times).
  useEffect(() => {
    delete globalWindow.__INITIAL_EVENT_HANDLE_FOR_EVENT_INFO_PROVIDER__;
    delete globalWindow.__INITIAL_EVENT_ID__;
    delete globalWindow.__INITIAL_EVENT_WEBSITE_STATE__;
    delete globalWindow.__INITIAL_EVENT_FEATURE_FLAGS__;
    delete globalWindow.__INITIAL_EVENT_TYPE__;
  }, []);

  return useMemo(() => {
    return {
      eventHandle: eventHandleRef.current,
      eventId: eventIdRef.current,
      eventWebsiteState: eventWebsiteStateRef.current,
      eventFeatureFlags: eventFeatureFlagsRef.current,
      eventType: eventTypeRef.current,
    };
  }, []);
};

/**
 * Loads event information and makes this data available to all its children.
 *
 * For more details, see [eventInfo/README.md](./README.md).
 */
export const EventInfoProvider: React.FC<EventInfoProviderProps> = ({
  eventHandle: eventHandleProp,
  initialEventId,
  initialEventFeatureFlags,
  initialEventWebsiteState,
  initialEventType,
  children,
}) => {
  const ssrEventInfo = useSsrEventInfo();
  const [eventHandle, setEventHandle] = useState<string | undefined>(() => eventHandleProp || ssrEventInfo.eventHandle);
  const { isAdmin } = useEventUserRole();

  const { getValueString } = useQueryParamHelper();
  const eventIdParam = getValueString(['eventId', 'event-id']);
  const { pathname } = useLocation();

  const isExcludedEventHandle = eventHandle ? EXCLUDED_EVENT_HANDLES.has(eventHandle) : false;
  const isOnAllowedEventIdRoute = EVENT_ID_PARAM_ALLOWED_ROUTES.some(pattern => matchPath(pathname, { path: pattern, exact: false, strict: false }));
  const shouldUseEventIdLookup = !!eventIdParam && isOnAllowedEventIdRoute && (!eventHandle || isExcludedEventHandle);

  const shouldSkipByName = shouldUseEventIdLookup || !eventHandle || isExcludedEventHandle;
  const shouldSkipById = !shouldUseEventIdLookup;

  const {
    data: dataByName,
    error: errorByName,
    loading: loadingByName,
  } = useBasicEventInfoQuery({
    batchMode: 'off',
    variables: { eventHandle: eventHandle || '', isAdmin: isPreviewing && isAdmin },
    // cache-first: during SSR the query executes and populates the cache.
    // On hydration, the client reads from the pre-populated cache — no double-fetch.
    // nextFetchPolicy ensures subsequent renders (e.g., after mutation) still hit the network.
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-and-network',
    ssr: true,
    skip: shouldSkipByName,
  });

  // SSR-safe: useQueryParamHelper reads from React Router's useLocation(), which
  // is populated by StaticRouter's location={request.url} on the server.
  // Only runs when shouldUseEventIdLookup is true (allow-listed handle-less route + valid param).
  const {
    data: dataById,
    error: errorById,
    loading: loadingById,
  } = useBasicEventInfoByIdQuery({
    batchMode: 'off',
    variables: { eventId: eventIdParam || '', isAdmin: isPreviewing && isAdmin },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-and-network',
    ssr: true,
    skip: shouldSkipById,
  });

  const eventData = shouldUseEventIdLookup ? dataById?.eventById : dataByName?.eventByName;
  const error = shouldUseEventIdLookup ? errorById : errorByName;
  const loading = shouldUseEventIdLookup ? loadingById : loadingByName;

  // IMPORTANT: This initializer depends on query data being available at mount time from
  // the SSR-populated Apollo cache. This works because:
  // 1. Both queries use fetchPolicy: 'cache-first' with ssr: true
  // 2. During SSR, renderToStringWithData resolves the active query and populates the cache
  // 3. On client hydration, cache-first reads from the pre-populated cache synchronously
  //
  // If fetchPolicy ever changes from cache-first, or if the provider tree order changes
  // such that the cache isn't populated by mount, isInitialLoad would incorrectly start
  // as true — causing downstream consumers (feature flags, header settings) to flash a
  // loading state.
  const [isInitialLoad, setIsInitialLoad] = useState(() => !eventData);

  const { executeSegmentGroupCallForEvent } = useSegmentGroupCallForEvent();
  executeSegmentGroupCallForEvent(eventData?.firebaseId, {
    postgresEventId: eventData?.id,
    eventType: eventData?.info.eventType,
  });

  const { identify } = useIdentify();
  useEffect(() => {
    const firebaseId = eventData?.firebaseId;
    if (firebaseId) {
      identify({
        lastFirebaseEventId: firebaseId,
      });
    }
  }, [eventData, executeSegmentGroupCallForEvent, identify]);

  const eventInfoData = useMemo<EventInfoData | undefined>(() => {
    if (!eventData) {
      return undefined;
    }

    const { firebaseId, id, info, firebaseCreateAt, productVerticals, settings, published, websiteState, featureFlags } = eventData;
    const {
      id: eventInfoId,
      eventType,
      eventDisplayName,
      eventPassword,
      fianceeFirstName,
      fianceeLastName,
      ownerFirstName,
      ownerLastName,
      locale,
      guestDisplayLocales,
      locationInfo,
      finalizedDate,
      story,
      qanda,
      tentativeDate,
    } = info;
    return {
      eventId: id,
      eventInfoId,
      eventFirebaseId: firebaseId,
      eventType,
      eventDisplayName,
      eventPassword: eventPassword ?? undefined,
      ownerFirstName,
      ownerLastName,
      fianceeFirstName,
      fianceeLastName,
      locale,
      guestDisplayLocales,
      locationInfo,
      firebaseCreateAt,
      productVerticals,
      finalizedDate,
      tentativeDate,
      story,
      qanda,
      published: published,
      settings,
      websiteState,
      featureFlags: (featureFlags || []).filter(Boolean) as string[],
    };
  }, [eventData]);

  const eventInfo = useMemo<EventInfo>(() => {
    return isInitialLoad
      ? {
          loading: true,
          ssrEventInfo: {
            eventId: initialEventId || ssrEventInfo.eventId,
            websiteState: initialEventWebsiteState || ssrEventInfo.eventWebsiteState,
            featureFlags: initialEventFeatureFlags || ssrEventInfo.eventFeatureFlags,
            eventType: ((initialEventType || ssrEventInfo.eventType) as EventType | undefined) ?? null,
          },
        }
      : {
          loading: !eventInfoData && loading,
          eventHandle,
          error,
          eventInfo: eventInfoData,
          finalizedEventDate: eventInfoData?.finalizedDate?.timestamp ? new Date(eventInfoData.finalizedDate.timestamp) : null,
        };
  }, [error, eventHandle, eventInfoData, isInitialLoad, loading, ssrEventInfo, initialEventId, initialEventFeatureFlags, initialEventWebsiteState, initialEventType]);

  const onFinishLoadingEventInfo = useCallback(() => {
    setIsInitialLoad(false);
  }, []);

  return (
    <InternalEventInfoProvider
      value={{
        setEventHandle,
        eventInfo,
        onFinishLoadingEventInfo,
      }}
    >
      {children}
    </InternalEventInfoProvider>
  );
};
